/**
 * CCLP Press — mobile Swiper carousel / desktop crossfade + autoplay.
 * Used by sections/cclp-press.liquid.
 */
(function () {
  const MOBILE_BREAKPOINT = 768;
  const AUTOPLAY_DELAY = 5000;
  const SWIPER_JS_URL = 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.js';
  const SWIPER_JS_INTEGRITY = 'sha384-hPkhOzAEx2ZcJuDkANAhjXmtEnqjkADleTxUbwTTJLmwmhV2F4/u6BOcnVL3xxqs';
  const SWIPER_CSS_URL = 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.css';
  const SWIPER_CSS_INTEGRITY = 'sha384-tOPXDsn4moKzmRcYpDt8mOcJWxzy89N8dK0oEkxTx90DZYjDdznDQ4IEiH1pcZH5';

  let assetsPromise = null;

  const loadPressStylesheet = (href) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve();
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.integrity = SWIPER_CSS_INTEGRITY;
      link.crossOrigin = 'anonymous';
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });

  const loadPressScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.integrity = SWIPER_JS_INTEGRITY;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

  const ensureSwiperAssets = () => {
    if (window.Swiper) return Promise.resolve();
    if (assetsPromise) return assetsPromise;
    assetsPromise = Promise.all([loadPressStylesheet(SWIPER_CSS_URL), loadPressScript(SWIPER_JS_URL)]).catch(
      (error) => {
        console.error('cclp-press: failed to load Swiper assets', error);
        assetsPromise = null;
      }
    );
    return assetsPromise;
  };

  class CclpPress extends HTMLElement {
    connectedCallback() {
      this._data = this._createInstance();
      if (!this._data) return;
      this._attachResize();
      this._attachVisibility();
    }

    disconnectedCallback() {
      const d = this._data;
      if (!d) return;

      if (d.desktopTimer) clearInterval(d.desktopTimer);
      if (d.resizeTimeout) clearTimeout(d.resizeTimeout);
      this._destroyMobile();

      d.logoHandlers.forEach(({ nav, handler }) => {
        nav.removeEventListener('click', handler);
      });
      if (d.resizeHandler) window.removeEventListener('resize', d.resizeHandler);
      if (d.visibilityHandler) document.removeEventListener('visibilitychange', d.visibilityHandler);

      this._data = null;
    }

    _createInstance() {
      const container = this.querySelector('.cclp-press__quotes-container');
      if (!container) return null;
      const wrapper = container.querySelector('.swiper-wrapper');
      if (!wrapper) return null;
      const slides = Array.from(wrapper.querySelectorAll('.cclp-press__quote-slide'));
      if (!slides.length) return null;

      const logoNavs = Array.from(this.querySelectorAll('.cclp-press__logo-nav'));
      const progressFill = this.querySelector('.cclp-press__progress-fill');
      const logosWrapper = this.querySelector('.cclp-press__desktop-logos-wrapper');

      slides.forEach((slide, idx) => {
        slide.dataset.slideIndex = idx;
        if (!slide.classList.contains('swiper-slide')) slide.classList.add('swiper-slide');
      });
      if (!wrapper.classList.contains('swiper-wrapper')) wrapper.classList.add('swiper-wrapper');

      const data = {
        container,
        wrapper,
        slides,
        logoNavs,
        progressFill,
        logosWrapper,
        swiper: null,
        desktopTimer: null,
        currentIndex: 0,
        mode: null,
        mobileInitPromise: null,
        logoHandlers: [],
        resizeHandler: null,
        visibilityHandler: null,
        resizeTimeout: null,
      };

      this._attachLogoHandlers(data);
      this._updateProgress(data, 0);
      this._updateMode(data);
      return data;
    }

    _attachLogoHandlers(data) {
      data.logoNavs.forEach((nav, idx) => {
        const handler = () => {
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            if (data.swiper) data.swiper.slideToLoop(idx);
          } else {
            this._stopDesktopAutoplay(data);
            this._showDesktopSlide(data, idx);
            this._startDesktopAutoplay(data);
          }
        };
        nav.addEventListener('click', handler);
        data.logoHandlers.push({ nav, handler });
      });
    }

    _updateProgress(data, index) {
      if (!data.progressFill || !data.slides.length) return;
      const segment = 100 / data.slides.length;
      data.progressFill.style.width = `${segment}%`;
      data.progressFill.style.marginLeft = `${segment * index}%`;
    }

    _centerDesktopLogo(data, index) {
      if (!data.logosWrapper || window.innerWidth < MOBILE_BREAKPOINT) return;
      const activeLogo = data.logoNavs[index];
      if (!activeLogo) return;
      requestAnimationFrame(() => {
        const wrapperWidth = data.logosWrapper.offsetWidth;
        const scrollPosition = activeLogo.offsetLeft - wrapperWidth / 2 + activeLogo.offsetWidth / 2;
        const maxScrollLeft = data.logosWrapper.scrollWidth - wrapperWidth;
        data.logosWrapper.scrollTo({
          left: Math.max(0, Math.min(scrollPosition, maxScrollLeft)),
          behavior: 'smooth',
        });
      });
    }

    _showDesktopSlide(data, index) {
      const total = data.slides.length;
      if (!total) return;
      const newIndex = ((index % total) + total) % total;
      const prevIndex = data.currentIndex;
      data.currentIndex = newIndex;
      if (data.slides[prevIndex]) data.slides[prevIndex].classList.remove('active');
      if (data.logoNavs[prevIndex]) data.logoNavs[prevIndex].classList.remove('active');
      if (data.slides[newIndex]) data.slides[newIndex].classList.add('active');
      if (data.logoNavs[newIndex]) data.logoNavs[newIndex].classList.add('active');
      this._updateProgress(data, newIndex);
      this._centerDesktopLogo(data, newIndex);
    }

    _stopDesktopAutoplay(data) {
      if (data.desktopTimer) {
        clearInterval(data.desktopTimer);
        data.desktopTimer = null;
      }
    }

    _startDesktopAutoplay(data) {
      if (data.slides.length <= 1) return;
      this._stopDesktopAutoplay(data);
      data.desktopTimer = setInterval(() => {
        this._showDesktopSlide(data, data.currentIndex + 1);
      }, AUTOPLAY_DELAY);
    }

    _destroyMobile() {
      const d = this._data;
      if (!d) return;
      if (d.swiper) {
        const idx = d.swiper.realIndex || 0;
        d.swiper.destroy(true, true);
        d.swiper = null;
        d.currentIndex = idx;
      }
      d.mobileInitPromise = null;
    }

    _enableDesktop(data) {
      if (data.mode === 'desktop') return;
      this._destroyMobile();
      if (data.currentIndex >= data.slides.length) data.currentIndex = 0;
      this._showDesktopSlide(data, data.currentIndex);
      this._startDesktopAutoplay(data);
      data.mode = 'desktop';
    }

    _enableMobile(data) {
      if (data.swiper) {
        data.mode = 'mobile';
        if (data.swiper.autoplay) data.swiper.autoplay.start();
        this._updateProgress(data, data.swiper.realIndex || 0);
        return;
      }
      if (data.mobileInitPromise) return;

      this._stopDesktopAutoplay(data);
      data.slides.forEach((s) => s.classList.remove('active'));
      data.logoNavs.forEach((n) => n.classList.remove('active'));

      data.mobileInitPromise = ensureSwiperAssets()
        .then(() => {
          data.mobileInitPromise = null;
          if (window.innerWidth >= MOBILE_BREAKPOINT || data.swiper) return;

          data.swiper = new window.Swiper(data.container, {
            loop: true,
            slidesPerView: 1,
            allowTouchMove: true,
            autoplay: { delay: AUTOPLAY_DELAY, disableOnInteraction: false },
            on: {
              init: (swiper) => {
                data.currentIndex = swiper.realIndex || 0;
                this._updateProgress(data, data.currentIndex);
              },
              slideChange: (swiper) => {
                data.currentIndex = swiper.realIndex || 0;
                this._updateProgress(data, data.currentIndex);
              },
            },
          });

          if (data.currentIndex > 0) data.swiper.slideToLoop(data.currentIndex, 0);
          data.mode = 'mobile';
        })
        .catch((error) => {
          console.error('cclp-press: mobile init failed', error);
          data.mobileInitPromise = null;
        });
    }

    _updateMode(data) {
      if (!this.isConnected) return;
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        this._enableMobile(data);
      } else {
        this._enableDesktop(data);
      }
    }

    _attachResize() {
      const d = this._data;
      d.resizeHandler = () => {
        clearTimeout(d.resizeTimeout);
        d.resizeTimeout = setTimeout(() => this._updateMode(d), 150);
      };
      window.addEventListener('resize', d.resizeHandler, { passive: true });
    }

    _attachVisibility() {
      const d = this._data;
      d.visibilityHandler = () => {
        if (document.hidden) {
          this._stopDesktopAutoplay(d);
          if (d.swiper && d.swiper.autoplay) d.swiper.autoplay.stop();
        } else {
          this._updateMode(d);
        }
      };
      document.addEventListener('visibilitychange', d.visibilityHandler);
    }
  }

  if (!customElements.get('cclp-press')) {
    customElements.define('cclp-press', CclpPress);
  }
})();
